import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Card,
  CardContent,
  CardActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tabs,
  Tab,
  Stack,
  Box
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Flag as MilestoneIcon,
  Link as DependencyIcon,
  Groups as TeamIcon,
  Timeline as FlowIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import useRoadmapStore from '../store/roadmapStore';
import PageContainer from '../components/PageContainer';
import StepPageHeader from '../components/StepPageHeader';
import { STEP_LABELS } from '../utils/constants';
import useAISuggest from '../hooks/useAISuggest';

const ComponentCard = ({ component, index, onEdit, onDelete, objectives }) => {
  const { t } = useTranslation();

  const getRelatedObjective = () =>
    objectives.find(obj => obj.name === component.related_objective)?.name || t('common.none');

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'Not Started': return 'default';
      default: return 'default';
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h6" component="h3" sx={{ flexGrow: 1 }}>
            {component.name || t('stepContent.components.card.untitled')}
          </Typography>
          <Chip 
            label={component.status || t('statusDetail.notStarted')} 
            color={getStatusColor(component.status)} 
            size="small" 
          />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {component.description || t('stepContent.components.card.noDescription')}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {t('stepContent.components.card.relatedObjective')} {getRelatedObjective()}
        </Typography>

        {component.milestones?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t('stepContent.components.card.milestones', { count: component.milestones.length })}
            </Typography>
            <List dense sx={{ py: 0 }}>
              {component.milestones.slice(0, 2).map((milestone, idx) => (
                <ListItem key={idx} sx={{ py: 0, px: 1 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <MilestoneIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={milestone.name} 
                    secondary={milestone.date}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
              {component.milestones.length > 2 && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                  +{component.milestones.length - 2} {t('common.more')}
                </Typography>
              )}
            </List>
          </Box>
        )}

        {component.dependencies?.length > 0 && (
          <Chip 
            icon={<DependencyIcon />}
            label={t('stepContent.components.card.dependencies', { count: component.dependencies.length })} 
            variant="outlined" 
            size="small" 
          />
        )}
      </CardContent>

      <CardActions>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(index)}>
          {t('common.edit')}
        </Button>
        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(index)}>
          {t('common.delete')}
        </Button>
      </CardActions>
    </Card>
  );
};

const ComponentForm = ({ component, objectives, components, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: component?.name || '',
    description: component?.description || '',
    related_objective: component?.related_objective || '',
    status: component?.status || 'Not Started',
    priority: component?.priority || 'Medium',
    start_date: component?.start_date || '',
    end_date: component?.end_date || '',
    expected_outcome: component?.expected_outcome || ''
  });
  const [milestones, setMilestones] = useState(component?.milestones || []);
  const [dependencies, setDependencies] = useState(component?.dependencies || []);
  const [newMilestone, setNewMilestone] = useState({ name: '', date: '' });

  const availableObjectives = objectives.map(obj => obj.name);
  const availableDependencies = components
    .filter((comp) => component ? comp.name !== component.name : true)
    .map(comp => comp.name);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onSave({ ...formData, milestones, dependencies });
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const addMilestone = () => {
    if (newMilestone.name.trim()) {
      setMilestones([...milestones, newMilestone]);
      setNewMilestone({ name: '', date: '' });
    }
  };

  const deleteMilestone = (index) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  return (
    <Paper elevation={2} sx={{ p: { xs: 2.5, md: 3 } }}>
      <Typography variant="h6" gutterBottom>
        {component ? t('stepContent.components.form.editTitle') : t('stepContent.components.form.addTitle')}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label={t('stepContent.components.form.fields.name')}
              value={formData.name}
              onChange={handleChange('name')}
              placeholder={t('stepContent.components.form.placeholders.name')}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('stepContent.components.form.fields.priority')}</InputLabel>
              <Select
                value={formData.priority}
                onChange={handleChange('priority')}
                label={t('stepContent.components.form.fields.priority')}
              >
                <MenuItem value="High">{t('priority.high')}</MenuItem>
                <MenuItem value="Medium">{t('priority.medium')}</MenuItem>
                <MenuItem value="Low">{t('priority.low')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('stepContent.components.form.fields.description')}
              value={formData.description}
              onChange={handleChange('description')}
              placeholder={t('stepContent.components.form.placeholders.description')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('stepContent.components.form.fields.relatedObjective')}</InputLabel>
              <Select
                value={formData.related_objective}
                onChange={handleChange('related_objective')}
                label={t('stepContent.components.form.fields.relatedObjective')}
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {availableObjectives.map((objective) => (
                  <MenuItem key={objective} value={objective}>
                    {objective}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('stepContent.components.form.fields.status')}</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange('status')}
                label={t('stepContent.components.form.fields.status')}
              >
                <MenuItem value="Not Started">{t('statusDetail.notStarted')}</MenuItem>
                <MenuItem value="In Progress">{t('statusDetail.inProgress')}</MenuItem>
                <MenuItem value="Completed">{t('statusDetail.completed')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('stepContent.components.form.fields.startDate')}
              type="date"
              value={formData.start_date}
              onChange={handleChange('start_date')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('stepContent.components.form.fields.endDate')}
              type="date"
              value={formData.end_date}
              onChange={handleChange('end_date')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('stepContent.components.form.fields.expectedOutcome')}
              value={formData.expected_outcome}
              onChange={handleChange('expected_outcome')}
              placeholder={t('stepContent.components.form.placeholders.expectedOutcome')}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1">
                  {t('stepContent.components.form.milestones.title')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    placeholder={t('stepContent.components.form.milestones.placeholder')}
                    value={newMilestone.name}
                    onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
                  />
                  <TextField
                    size="small"
                    type="date"
                    value={newMilestone.date}
                    onChange={(e) => setNewMilestone({ ...newMilestone, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button onClick={addMilestone} disabled={!newMilestone.name.trim()}>
                    <AddIcon />
                  </Button>
                </Stack>
              </Box>

              <Stack spacing={1}>
                {milestones.map((milestone, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">{milestone.name}</Typography>
                      {milestone.date && (
                        <Typography variant="caption" color="text.secondary">
                          {milestone.date}
                        </Typography>
                      )}
                    </Box>
                    <IconButton size="small" onClick={() => deleteMilestone(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {milestones.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('stepContent.components.form.milestones.empty')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t('stepContent.components.form.fields.dependencies')}</InputLabel>
              <Select
                multiple
                value={dependencies}
                onChange={(e) => setDependencies(e.target.value)}
                label={t('stepContent.components.form.fields.dependencies')}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {availableDependencies.map((dep) => (
                  <MenuItem key={dep} value={dep}>
                    {dep}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
              sx={{ '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}>
              <Button type="submit" variant="contained" disabled={!formData.name.trim()}>
                {component ? t('common.update') : t('common.add')}
              </Button>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default function ComponentsPage() {
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted 
  } = useRoadmapStore();
  const { t } = useTranslation();
  const handleAISuggest = useAISuggest('action_components');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.action_components);

  const stepData = getStepData('action_components');
  const objectivesData = getStepData('strategic_objectives');

  const [components, setComponents] = useState(stepData.components || []);
  const [objectives] = useState(objectivesData.objectives || []);
  const [isComplete, setIsComplete] = useState(isStepCompleted('action_components'));
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('components');

  useEffect(() => {
    const data = getStepData('action_components');
    setComponents(data.components || []);
    setIsComplete(isStepCompleted('action_components'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const updateData = (newComponents) => {
    setComponents(newComponents);
    updateStepData('action_components', {
      components: newComponents
    });
  };

  const handleSaveComponent = (componentData) => {
    if (editingIndex !== null) {
      const updated = components.map((comp, index) => 
        index === editingIndex ? componentData : comp
      );
      updateData(updated);
    } else {
      updateData([...components, componentData]);
    }
    
    setShowForm(false);
    setEditingIndex(null);
  };

  const handleEditComponent = (index) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleDeleteComponent = (index) => {
    if (window.confirm(t('common.confirmDelete'))) {
      const updated = components.filter((_, i) => i !== index);
      updateData(updated);
    }
  };

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('action_components', newCompleteState);
    setIsComplete(success);
  };

  const canComplete = components.length > 0;

  const renderComponentsView = () => (
    <Stack spacing={3}>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => {
          setEditingIndex(null);
          setShowForm(true);
        }}
      >
        {t('stepContent.components.actions.addComponent')}
      </Button>

      {showForm && (
        <ComponentForm
          component={editingIndex !== null ? components[editingIndex] : null}
          objectives={objectives}
          components={components}
          onSave={handleSaveComponent}
          onCancel={() => {
            setShowForm(false);
            setEditingIndex(null);
          }}
        />
      )}

      {components.length > 0 ? (
        <Grid container spacing={3}>
          {components.map((component, index) => (
            <Grid item xs={12} md={6} key={index}>
              <ComponentCard
                component={component}
                index={index}
                objectives={objectives}
                onEdit={handleEditComponent}
                onDelete={handleDeleteComponent}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        !showForm && (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('stepContent.components.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('stepContent.components.empty.description')}
            </Typography>
          </Paper>
        )
      )}
    </Stack>
  );

  const renderDependenciesView = () => (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <FlowIcon />
        <Typography variant="h5">
          {t('stepContent.components.sections.flow.title')}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {t('stepContent.components.sections.flow.description')}
      </Typography>
    </Paper>
  );

  const renderTeamView = () => (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <TeamIcon />
        <Typography variant="h5">
          {t('stepContent.components.sections.team.title')}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {t('stepContent.components.sections.team.description')}
      </Typography>
    </Paper>
  );

  return (
    <PageContainer maxWidth={1300}>
      <StepPageHeader
        stepNum={4}
        title={t(STEP_LABELS.action_components)}
        intro={t('stepContent.components.intro')}
        isComplete={isComplete}
        canComplete={canComplete}
        onToggleComplete={handleCompleteToggle}
        requireMessage={t('stepContent.components.alerts.requireItem')}
      onAISuggest={handleAISuggest}
        />
      <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, mb: 3 }}>
        {objectives.length === 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('stepContent.components.alerts.noObjectives')}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(e, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3 }}
        >
          <Tab value="components" label={t('stepContent.components.tabs.components')} />
          <Tab value="dependencies" label={t('stepContent.components.tabs.dependencies')} />
          <Tab value="team" label={t('stepContent.components.tabs.team')} />
        </Tabs>

        {activeTab === 'components' && renderComponentsView()}
        {activeTab === 'dependencies' && renderDependenciesView()}
        {activeTab === 'team' && renderTeamView()}
      </Paper>
    </PageContainer>
  );
}
